/**
 * The recall list as a worklist rather than a report (KIN-1890, backend KIN-1859).
 *
 * Without state the list is the same every week: a practice that rang forty families on
 * Monday sees the same forty on Tuesday, with no way to tell who has already been
 * contacted, who asked to be called back after the holidays, and whose number does not
 * work. That is how a useful report becomes one nobody opens.
 *
 * State is per patient AND per course. A parent chased about MMR has not been chased about
 * the 6-in-1, and the row already lists the outstanding courses separately.
 *
 * None of this is clinical truth. What a child has received is always the doses on the
 * record — there is deliberately no "completed" state, because when the dose is recorded
 * the slot becomes GIVEN and the child leaves the list unaided.
 */

export const RECALL_STATE = {
  notified: "NOTIFIED",
  snoozed: "SNOOZED",
  unreachable: "UNREACHABLE",
};

export const RECALL_STATE_OPTIONS = [
  {
    value: RECALL_STATE.notified,
    label: "Contacted",
    description: "The family has been told this dose is outstanding",
  },
  {
    value: RECALL_STATE.snoozed,
    label: "Call back later",
    description: "Hide this course until a date you choose",
  },
  {
    value: RECALL_STATE.unreachable,
    label: "Cannot reach them",
    description: "A dead number or no contact details — a record to fix, not a call to make",
  },
];

// Free text on the wire, capped at 30 by the backend. Offered as suggestions rather than a
// closed list: a practice that writes "Text via reception" should be able to.
export const NOTIFICATION_METHODS = ["SMS", "Phone", "Letter", "Email", "In person"];

export const MAX_NOTIFICATION_METHOD_LENGTH = 30;
export const MAX_NOTE_LENGTH = 1000;

/** The state recorded against one course of one patient, or null where nobody has been chased. */
export const recallStateForCourse = (recall, courseKey) =>
  (recall?.recallStates || []).find((state) => state.courseKey === courseKey) ??
  null;

export const hasAnyRecallState = (recall) =>
  (recall?.recallStates || []).length > 0;

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * A snoozed course is hidden until its date passes, whatever the list is asking for —
 * that is what snoozing means. Mirrors `VaccineRecallStateDTO.hiddenOn` on the backend,
 * including its boundary: the course reappears the day AFTER snoozeUntil.
 */
export const isSnoozeActive = (state, today = new Date()) => {
  if (state?.state !== RECALL_STATE.snoozed || !state?.snoozeUntil) return false;

  const until = new Date(`${state.snoozeUntil}T00:00:00`);

  if (Number.isNaN(until.getTime())) return false;

  return startOfDay(today).getTime() <= until.getTime();
};

/**
 * What the row says about a course that has been worked.
 *
 * `notifiedCount` earns its place: a third attempt is a different conversation from a
 * first, and the backend only increments it for NOTIFIED — snoozing or flagging
 * unreachable does not inflate it.
 */
export const formatRecallState = (state, formatDate) => {
  if (!state?.state) return null;

  if (state.state === RECALL_STATE.unreachable) return "Cannot reach them";

  if (state.state === RECALL_STATE.snoozed) {
    return state.snoozeUntil
      ? `Call back after ${formatDate(state.snoozeUntil)}`
      : "Call back later";
  }

  const attempts = state.notifiedCount > 1 ? ` ×${state.notifiedCount}` : "";
  const method = state.notificationMethod ? ` by ${state.notificationMethod}` : "";

  return `Contacted${attempts}${method}`;
};

/**
 * The payload for PUT /recall/{patientId}/{courseKey}.
 *
 * `snoozeUntil` travels only with SNOOZED: it is what the course is hidden until, and on
 * any other state it is a value the backend has no use for. Blank free text is omitted
 * rather than sent as an empty string, so "not recorded" stays distinct from "recorded as
 * nothing".
 */
export const buildRecallStateRequest = ({
  state,
  notificationMethod,
  snoozeUntil,
  note,
}) => {
  const method = notificationMethod?.trim();
  const trimmedNote = note?.trim();

  return {
    state,
    ...(method ? { notificationMethod: method } : {}),
    ...(state === RECALL_STATE.snoozed && snoozeUntil ? { snoozeUntil } : {}),
    ...(trimmedNote ? { note: trimmedNote } : {}),
  };
};

/**
 * Why the form cannot be submitted yet, or null when it can.
 *
 * A date is mandatory for SNOOZED and the backend refuses without one. Saying so here
 * turns a 400 into a sentence beside the field.
 */
export const recallStateError = ({ state, notificationMethod, snoozeUntil, note }) => {
  if (!state) return "Choose what happened.";

  if (state === RECALL_STATE.snoozed && !snoozeUntil) {
    return "Choose the date to call back after.";
  }

  if ((notificationMethod?.trim().length ?? 0) > MAX_NOTIFICATION_METHOD_LENGTH) {
    return `Keep the method under ${MAX_NOTIFICATION_METHOD_LENGTH} characters.`;
  }

  if ((note?.trim().length ?? 0) > MAX_NOTE_LENGTH) {
    return `Keep the note under ${MAX_NOTE_LENGTH} characters.`;
  }

  return null;
};
